import { get, isEmpty, last, reduce, cloneDeep, map, groupBy, sumBy, head } from 'lodash'
import moment from 'moment'
import creditCardType from 'credit-card-type'

import { getModifiersWithItems, getUserNameFromCard } from '../utils/orderUtils'
import OrderStates from '../utils/orderStates'
import PaymentTypes from '../utils/paymentTypes'
import { centsToDollars, stringFloatToCents, toFixed } from '../utils/formatters'

const lastNth = (string, count = 1) => {
  return string.substring(string.length - count)
}

const getId = (order) => get(order, 'uuid', new Date().getTime())

const getOrderId = (order) => get(order, 'uuid', 'unknown')

const orderIsClosed = (order) => OrderStates.isClosed(order.state)

const getPayments = (order) => order.paymentInfo

const getRemainingBalanceInCents = (order) => {
  return stringFloatToCents(order.remainingBalance)
}

const getRemainingBalance = (order) => {
  return centsToDollars(getRemainingBalanceInCents(order))
}

const deducePaymentInfo = (order) => {
  if (order.paymentDisplay) {
    return order.paymentDisplay
  }

  return orderIsClosed(order) ? 'Paid with cash' : 'Payment on file'
}

const formatCardReaderDisplay = (order, type) => {
  const userAttributes = get(order, 'userAttributes', {})
  const cardReaderData = get(order, 'cardReaderData', {})

  let lastFour = userAttributes.last4
  if (!lastFour) lastFour = lastNth(get(cardReaderData, 'receiptData.maskedCardNumber', '----'), 4)

  let cardIssuer = userAttributes.freedompayIssuerName?.trim?.()
  if (!cardIssuer) cardIssuer = order?.cardReaderData?.cardIssuer ? order.cardReaderData.cardIssuer : order?.cardReaderData?.cardType

  let cardholderName = order?.userName?.trim?.()
  if (!cardholderName) cardholderName = getUserNameFromCard(cardReaderData)

  if (type === 'row') {
    return lastFour
  }

  if (type === 'detail' || type === undefined) {
    return `${cardholderName}${isEmpty(cardholderName) ? '' : ' / '}${cardIssuer} ${lastFour}`
  }
}

const getPaymentType = (order) => {
  const validPayments = order?.paymentInfo?.filter?.(payment => payment.amountInCents > 0) ?? []
  return last(validPayments)?.paymentType
}

/**
 * @param {object} order
 * @param {'detail'|'row'|undefined} type
 */
const getPayment = (order, type) => {
  if (order?.state === 'authorization_failed' && type !== undefined) return 'Card Declined'
  
  const payments = getPayments(order)
  const validPayments = payments?.filter?.(payment => payment.amountInCents > 0)
  const validPaymentsCount = validPayments?.length ?? 0

  if (validPaymentsCount === 0 && !isEmpty(payments)) return 'Card Required'

  const cardReaderData = get(order, 'cardReaderData', {})
  const paymentType = getPaymentType(order)

  const cardWasSwiped = PaymentTypes.isCardReader(paymentType) || (!isEmpty(cardReaderData) && !paymentType)
  if (cardWasSwiped) return formatCardReaderDisplay(order, type)

  return deducePaymentInfo(order)
}

export const getRowPayment = (order) => getPayment(order, 'row')

const getDetailPayment = (order) => getPayment(order, 'detail')

const getAmount = (order) => centsToDollars(order?.amountInCents ?? 0) || '--'

const getModifiedAt = (order) => {
  let modifiedAt = get(order, 'modifiedAt', 'unknown')

  if (modifiedAt !== 'unknown') {
    modifiedAt = moment(modifiedAt).format('h:mm a')
  }

  return modifiedAt
}

const getOrderNumber = (order) => get(order, 'orderNumber', 'unknown')

const getSyncStatus = (order) => !get(order, 'syncedAt') ? 'unsynced' : 'synced'

const getOrderStatus = (order) => orderIsClosed(order) ? 'closed' : 'open'

const getItemCount = (order) => {
  const count = order?.itemModels?.length ?? 0
  const fractionalQuantity = order?.itemModels?.[0]?.fractionalQuantity ?? 1
  return parseFloat(toFixed(count * fractionalQuantity, 2))
}

const getSubtotal = (order) => {
  const itemModels = getItems(order)

  const priceInCents = reduce(itemModels, (accumulator, item) => {
    const fractionalQuantity = get(item, 'fractionalQuantity', 0)
    const itemModifiers = get(item, 'modifiers', [])
    const itemSubtotal = get(item, 'priceInCents', 0) * fractionalQuantity

    const modifiersSubtotal = reduce(itemModifiers, (acc, modifierItem) => {
      const priceInCents = get(modifierItem, 'defaultPriceInCents', 0)
      return acc + priceInCents * fractionalQuantity
    }, 0)

    const lineItemTotal = itemSubtotal + modifiersSubtotal

    return accumulator + lineItemTotal
  }, 0)

  return centsToDollars(priceInCents) || '--'
}

const getTip = (order) => {
  const tip = get(order, 'tipAmountInCents')
  if (tip) return centsToDollars(tip)

  return '-'
}

const getItems = (order) => {
  const lineItems = order?.lineItems ?? []
  const itemModels = order?.itemModels ?? []
  const result = itemModels.map((item) => {
    const matchingLineItem = lineItems.find((lineItem) => lineItem.uuid === item.lineItemUuid)
    const fractionalQuantity = matchingLineItem?.fractionalQuantity ?? 1
    const quantity = item.quantity

    return {
      ...item,
      quantity,
      fractionalQuantity,
    }
  })

  return result
}

const getItemsWithModifiers = (order) => {
  const itemModels = getItems(order)
  return getModifiersWithItems(itemModels).items
}

export const getGroupedItems = (order, options) => {
  const itemModels = getItems(order)
  const newItems = map(
    groupBy(itemModels, item => item.id + item.modifiersString + item.price),
    (item) => {
      const reducedItem = {
        ...item[0],
        fractionalQuantity: parseFloat(toFixed(sumBy(item, 'fractionalQuantity'), 2)),
        quantity: sumBy(item, 'fractionalQuantity'),
      }

      if (options?.sumGroupedItems) {
        let priceInCents = sumBy(item, 'priceInCents')

        if (options?.includeModifierPrices) {
          priceInCents += reduce(item, (acc, lineItem) => acc + sumBy(lineItem.modifiers, 'defaultPriceInCents'), 0)
        }

        reducedItem.price = centsToDollars(priceInCents * item[0].fractionalQuantity)
      }


      if (options?.excludeFreeModifiers) {
        const result = (reducedItem.modifiers ?? []).reduce((modifiers, modifier) => {
          const { name, defaultPriceInCents, price } = modifier

          if (modifier?.defaultPriceInCents > 0) {
            return [...modifiers, `${name} + ${centsToDollars(defaultPriceInCents)}`]
          } else if (stringFloatToCents(modifier?.price ?? '') > 0) {
            return [...modifiers, `${name} + ${price}`]
          } else {
            return modifiers
          }
        }, [])

        reducedItem.modifiersString = result.join(', ')
      }

      return reducedItem
    }
  )

  return newItems
}

const getCustomerId = (order) => get(order, 'customerId')

const getRevenueCenterUuid = (order) => get(order, 'standUuid')

const getMenuUuid = (order) => get(order, 'standMenuUuid') || get(order, 'createOrderParams.standMenuUuid')

export const getCreditCardType = (order) => {
  const firstSix = order?.userAttributes?.maskedPan?.slice(0, 6)
  const cardTypes = creditCardType(firstSix)
  return head(cardTypes)?.niceType
}

const getIsMultiPayment = (order) => {
  const payments = getPayments(order)
  const remainingBalanceInCents = getRemainingBalanceInCents(order)
  const validPayments = payments?.filter?.(payment => payment.amountInCents > 0)
  const validPaymentsCount = validPayments?.length ?? 0
  const hasRemainingBalance = remainingBalanceInCents > 0 || validPaymentsCount > 1

  if (validPaymentsCount === 0) {
    return false
  } else {
    return hasRemainingBalance
  }
}

const TabOrderRowViewModel = ((order) => {
  const clonedOrder = cloneDeep(order)

  return {
    id: getId(clonedOrder),
    orderId: getOrderId(clonedOrder),
    payment: getRowPayment(clonedOrder),
    amount: getAmount(clonedOrder),
    splittableAmount: clonedOrder?.amountInCents ?? 0,
    modifiedAt: getModifiedAt(clonedOrder),
    orderNumber: getOrderNumber(clonedOrder),
    syncStatus: getSyncStatus(clonedOrder),
    orderStatus: getOrderStatus(clonedOrder),
    orderType: clonedOrder.orderType,
    creditCardType: getCreditCardType(clonedOrder),
    isMultiPayment: getIsMultiPayment(clonedOrder),
    userNotes: clonedOrder?.userNotes,
    errorMessage: clonedOrder.errorMessage ?? '',
  }
})

const defaultOptions = {
  sumGroupedItems: false,
  includeModifierPrices: false,
  excludeFreeModifiers: false,
}

const TabOrderDetailsViewModel = (order, options = defaultOptions) => {
  const clonedOrder = cloneDeep(order)

  const result = {
    id: getId(clonedOrder),
    orderId: getOrderId(clonedOrder),
    payment: getDetailPayment(clonedOrder),
    unfilteredPayment: getPayment(clonedOrder),
    paymentType: getPaymentType(clonedOrder),
    amount: getAmount(clonedOrder),
    splittableAmount: clonedOrder?.amountInCents ?? 0,
    modifiedAt: getModifiedAt(clonedOrder),
    orderNumber: getOrderNumber(clonedOrder),
    syncStatus: getSyncStatus(clonedOrder),
    orderStatus: getOrderStatus(clonedOrder),
    subtotal: getSubtotal(clonedOrder),
    total: getAmount(clonedOrder),
    tip: getTip(clonedOrder),
    itemCount: getItemCount(clonedOrder),
    items: getItems(clonedOrder),
    itemsWithModifiers: getItemsWithModifiers(clonedOrder),
    groupedItems: getGroupedItems(clonedOrder, options),
    customerId: getCustomerId(clonedOrder),
    revenueCenterUuid: getRevenueCenterUuid(clonedOrder),
    menuUuid: getMenuUuid(clonedOrder),
    remainingBalance: getRemainingBalance(clonedOrder),
    remainingBalanceInCents: getRemainingBalanceInCents(clonedOrder),
    payments: getPayments(clonedOrder),
    isMultiPayment: getIsMultiPayment(clonedOrder),
    orderTotalFromOrder: clonedOrder.orderTotalFromOrder,
    totalAmountInCents: clonedOrder.orderTotalFromOrder?.totalAmountInCents,
    remoteOrderTotal: clonedOrder.remoteOrderTotal,
    orderType: clonedOrder.orderType,
    exemptionOverrides: clonedOrder.exemptionOverrides,
    orderState: clonedOrder.state,
    paymentAuthorizedAt: clonedOrder.paymentAuthorizedAt,
    pendingMutation: clonedOrder.pendingMutation,
    frozen: clonedOrder.frozen,
    userNotes: clonedOrder.userNotes ?? null,
    errorMessage: clonedOrder.errorMessage ?? '',
  }

  return result
}

export { TabOrderRowViewModel, TabOrderDetailsViewModel }
